
/**
 * Applies markdown formatting to text for rendering as HTML
 * Supports: 
 * - Bold (**text**)
 * - Italic (*text*)
 * - Bullet points (- item)
 * - Numbered lists (1. item)
 * - Links ([text](url))
 * - Headers (# Header)
 */
export const applyMarkdownFormatting = (text: string): string => {
  if (!text) return '';
  
  let formattedText = text
    // Convert line breaks to <br> tags
    .replace(/\n/g, '<br>')
    
    // Bold: **text** to <strong>text</strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    
    // Italic: *text* to <em>text</em> (but not if it's part of bold **)
    .replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
    
    // Headers (simple implementation for h3, h4)
    .replace(/### (.*?)(<br>|$)/g, '<h3>$1</h3>')
    .replace(/## (.*?)(<br>|$)/g, '<h2>$1</h2>')
    .replace(/# (.*?)(<br>|$)/g, '<h1>$1</h1>')
    
    // Links: [text](url) to <a href="url">text</a>
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-primary hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
    
    // Inline code: `code` to <code>code</code>
    .replace(/`(.*?)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-sm">$1</code>');
  
  // Process lists - this is more complex as we need to wrap items
  const lines = formattedText.split('<br>');
  let inBulletList = false;
  let inNumberedList = false;
  
  for (let i = 0; i < lines.length; i++) {
    // Bullet list
    if (lines[i].trim().startsWith('- ')) {
      if (!inBulletList) {
        lines[i] = '<ul class="list-disc pl-5 my-2">' + lines[i];
        inBulletList = true;
      }
      lines[i] = lines[i].replace(/^- (.*)$/, '<li>$1</li>');
    } 
    // Numbered list
    else if (lines[i].trim().match(/^\d+\. /)) {
      if (!inNumberedList) {
        lines[i] = '<ol class="list-decimal pl-5 my-2">' + lines[i];
        inNumberedList = true;
      }
      lines[i] = lines[i].replace(/^\d+\. (.*)$/, '<li>$1</li>');
    } 
    // End lists if we encounter a non-list item
    else {
      if (inBulletList) {
        lines[i-1] += '</ul>';
        inBulletList = false;
      }
      if (inNumberedList) {
        lines[i-1] += '</ol>';
        inNumberedList = false;
      }
    }
  }
  
  // Close any open lists at the end
  if (inBulletList) {
    lines[lines.length-1] += '</ul>';
  }
  if (inNumberedList) {
    lines[lines.length-1] += '</ol>';
  }
  
  return lines.join('<br>');
};
